import { useCallback } from 'react';

import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import { getApiErrorCode } from '../../../utils/apiErrorCode';

import AiDocumentChat from '../../../components/Ai/AiDocumentChat';
import { AiClassificationStatusEnum } from '../../../enums/AiClassificationStatusEnum';
import type File from '../../../interfaces/File';
import type { ClassifyFilesResponse } from '../../../interfaces/FileApiService';
import { isAiClassifiedFile } from '../../../lib/fileActions';
import { useRightPanel } from '../../useRightPanel';
import { useAppStore } from '../../../store/store';
import { openAiSummaryModal } from '../../../utils/openAiSummaryModal';
import type { UseFilesActionsDeps } from '../deps';
import { fileService } from '../services';
import type { UseFilesActionsEvents, UseFilesActionsReturn } from '../types';

/**
 * Provides AI-related actions for files, such as classify, summarize, and document chat.
 */
export function useFilesAiActions(
  deps: UseFilesActionsDeps,
): Pick<
  UseFilesActionsReturn,
  'handleAiClassifyFile' | 'handleAiSummaryFile' | 'handleAiDocumentChatFile'
> {
  const { modal, notification, message, t } = deps;
  const { openRightPanel, setRightPanelContent, setRightPanelKind } = useRightPanel();

  /**
   * Attempts to classify a set of files using the AI classification service.
   * Handles classification results and triggers corresponding events or notifications.
   */
  const aiClassifyFile = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      const hasFolder = files.some((f) => f.isFolder);

      /**
       * Returns an appropriate success message based on file and folder selection.
       */
      const getSuccessMessage = (queuedCount: number, skippedCount: number) => {
        const effectiveCount = queuedCount > 0 ? queuedCount : files.length;
        const isMultipleFiles = effectiveCount > 1;

        if (hasFolder && files.length === 1) {
          return t('notifications.folderClassifyingBatch');
        } else if (hasFolder) {
          return t('notifications.foldersClassifyingBatch', { count: files.length });
        } else if (isMultipleFiles) {
          return skippedCount > 0
            ? t('notifications.filesClassifyingBatchWithSkipped', {
                count: effectiveCount,
                skipped: skippedCount,
              })
            : t('notifications.filesClassifyingBatch', { count: effectiveCount });
        } else {
          return t('notifications.fileClassifying');
        }
      };

      const fileIds = files.map((f) => f.id);

      // Request classification from the backend service for all selected files. The backend skips
      // documents it has already classified, so a selection containing one forces a re-classification
      // (previous AI tags are replaced) instead of asking the user to pick between the two actions.
      fileService
        .classify(fileIds, files.some(isAiClassifiedFile))
        .then((response: ClassifyFilesResponse) => {
          const queuedCount = response.filesQueued ?? files.length;
          const skippedCount = response.filesSkipped ?? 0;

          // Mark files as 'Queued' in the UI until realtime events advance them.
          const updatedFiles: File[] = files.map((file) => ({
            ...file,
            aiOperationStatus: AiClassificationStatusEnum.Queued,
            aiOperationErrorMessage: undefined,
          }));
          events?.onFilesUpdated?.(updatedFiles);

          // Show a success message to the user
          message.success({
            content: getSuccessMessage(queuedCount, skippedCount),
          });

          // Refresh batches for real-time updates elsewhere in the app
          useAppStore.getState().events.emit.refreshBatches();
        })
        .catch((error: { response?: string | Record<string, unknown> }) => {
          const errorCode = getApiErrorCode(error.response);
          const errorMessage =
            error?.response && typeof error.response === 'object'
              ? (error.response as Record<string, unknown>).message?.toString()
              : undefined;

          switch (errorCode) {
            case ApiErrorEnum.FilesAlreadyClassified:
              message.info(errorMessage || t('errors.filesAlreadyClassified'));
              break;
            case ApiErrorEnum.FilesBeingClassified:
            case ApiErrorEnum.FilesBeingClassifiedByOthers:
            case ApiErrorEnum.FilesBeingClassifiedByMe:
              notification.warning({
                title: errorMessage || t('errors.filesBeingClassified'),
                description: t('errors.filesBeingClassifiedDescription'),
              });
              break;
            case ApiErrorEnum.FilesTooBig:
              notification.error({
                title: errorMessage || t('errors.filesTooBig'),
                description: t('errors.filesTooBigDescription'),
              });
              break;
            case ApiErrorEnum.TooManyFiles: {
              const payload =
                error?.response && typeof error.response === 'object'
                  ? (error.response as Record<string, unknown>)
                  : {};
              const files =
                typeof payload.files === 'number'
                  ? payload.files
                  : typeof payload.Files === 'number'
                    ? payload.Files
                    : undefined;
              const limit =
                typeof payload.limit === 'number'
                  ? payload.limit
                  : typeof payload.Limit === 'number'
                    ? payload.Limit
                    : undefined;
              notification.warning({
                title: errorMessage || t('errors.tooManyFiles'),
                description: t('errors.tooManyFilesDescription', {
                  files: files ?? '—',
                  limit: limit ?? '—',
                }),
              });
              break;
            }
            default:
              notification.error({
                title: t('errors.genericFetchError', {
                  resource: t('common.files'),
                }),
              });
              break;
          }
        })
        .finally(() => {
          // No cleanup or finalization required here
        });
    },
    [fileService, t, notification, message],
  );

  /**
   * Opens a modal showing the AI summary of the given file.
   * Notifies parent components when the summary is generated.
   */
  const aiSummaryFile = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      openAiSummaryModal({
        modal,
        t,
        file,
        onSummaryGenerated: () => {
          // Trigger event to notify parent that a summary was generated
          const files: File = {
            ...file,
          };
          events?.onFilesUpdated?.([files]);
        },
      });
    },
    [modal, t],
  );

  /**
   * Opens document AI chat in the right panel (same host as Viewer/Office).
   * FilesHeader keeps content in sync with selection while kind is aiDocumentChat.
   */
  const aiDocumentChat = useCallback(
    (file: File) => {
      setRightPanelKind('aiDocumentChat');
      setRightPanelContent(<AiDocumentChat key={file.id} file={file} />);
      openRightPanel();
    },
    [openRightPanel, setRightPanelContent, setRightPanelKind],
  );

  /**
   * Handles AI classification action triggered from file manager or UI.
   */
  const handleAiClassifyFile = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      aiClassifyFile(files, events);
    },
    [aiClassifyFile],
  );

  /**
   * Handles requesting an AI-generated summary for a file.
   */
  const handleAiSummaryFile = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      aiSummaryFile(file, events);
    },
    [aiSummaryFile],
  );

  /**
   * Handles showing the AI chat right panel for an individual file.
   */
  const handleAiDocumentChatFile = useCallback(
    (file: File) => {
      aiDocumentChat(file);
    },
    [aiDocumentChat],
  );

  // Expose handlers for file manager integration
  return {
    handleAiClassifyFile,
    handleAiSummaryFile,
    handleAiDocumentChatFile,
  };
}
